import React, { useCallback, useEffect, useRef, useState } from "react";
import { IconEdit, ItemCheck } from "../../components/CustomIcons";
// import { useNavigate } from "react-router-dom"; // navigation not used currently
import { ContentItem } from "../../types";
import SearchPanel from "../../components/SearchPanel/SearchPanel";
import { FL_RATING_OPTIONS } from "../../constants/flRatingOptions";
import {
  getDefaultPunishmentsImages,
  getContentByType,
} from "../../services/firebase";
import badge1 from "../../assets/badge_1.png";
import badge2 from "../../assets/badge_2.png";
import badge3 from "../../assets/badge_3.png";
import badge4 from "../../assets/badge_4.png";
import badge5 from "../../assets/badge_5.png";

import Cropper from "react-easy-crop";
import { validateImageFile } from "../../components/utils/imageValidation";
import { resizeImage } from "../../components/utils/resizeImage";
import { cropImageFile } from "../../components/utils/cropImage";
// import { updateLeaguePunishment } from "../../components/leagueLocker/firestoreQuery"; // handled by parent
import { flRatingOptions } from "../../constants/leagueLockerConfig";
import MarkDownWithOption from "../ui/MarkDownWithOption";

interface SelectPunishmentProps {
  leagueData: {
    id: string;
    [key: string]: any;
  };
  setSelectedTab: (tab: number) => void;
  // New prop to bubble up the full punishment object to parent
  setPunishmentData?: (data: any) => void;
  punishmentData?: any;
}

const SelectPunishment: React.FC<SelectPunishmentProps> = ({
  leagueData,
  setSelectedTab,
  setPunishmentData,
  punishmentData,
}) => {
  const [punishments, setPunishments] = useState<ContentItem[]>([]);
  const [filteredPunishments, setFilteredPunishments] = useState<ContentItem[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFLRating, setSelectedFLRating] = useState<string>("");
  // const navigate = useNavigate(); // currently not used; kept for future navigation needs
  const [displayCount, setDisplayCount] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);
  const [embarrassmentLevel, setEmbarrassmentLevel] = useState(0);
  const [riskLevel, setRiskLevel] = useState(0);
  const [longTermEffects, setLongTermEffects] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [defaultCardImage, setDefaultCardImage] = useState<string>("");

  const [isCustomPunishment, setIsCustomPunishment] = useState<boolean>(false);
  // Initialize selectedPunishment from leagueData.leaguePunishment if present, else null
  // const [selectedPunishment, setSelectedPunishment] = useState<any>(
  //   leagueData?.leaguePunishment || null
  // );
  // Show cardImage as preview if present in initial data
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showDropDown, setShowDropdown] = useState<boolean>(false);
  const badgeImages = [badge1, badge2, badge3, badge4, badge5];
  // const punishmentPhotoRef = useRef<HTMLInputElement | null>(null); // unused

  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  // const [loadingCropper, setLoadingCropper] = useState(false); // spinner optional
  const [punishmentImage, setPunishmentImage] = useState<File | null>(null);
  // const [isSaving, setIsSaving] = useState(false); // saving handled by parent now
  const punishmentImageRef = useRef<HTMLInputElement | null>(null);
  // const [customImagePreview, setCustomImagePreview] = useState<string | null>(null);
  const [selectedCustomPunishment, setSelectedCustomPunishment] =
    useState<any>(null);
  const [selectedPunishmentFromGallery, setSelectedPunishmentFromGallery] =
    useState<any>(null);

  // Sync incoming punishmentData from parent (edit mode hydrate). Avoid adding isCustomPunishment
  // as dependency so local toggles don't retrigger hydration and overwrite user edits.
  useEffect(() => {
    if (!punishmentData) return;
    if (punishmentData?.isCustomPunishment !== undefined) {
      setIsCustomPunishment(punishmentData.isCustomPunishment);
    }
    if (punishmentData.isCustomPunishment) {
      setSelectedCustomPunishment(punishmentData);
      // Preserve existing preview if we already have one (e.g., user just cropped/uploaded)
      setImagePreview(
        (prev) =>
          prev ||
          punishmentData.previewImage ||
          punishmentData.cardImage ||
          null
      );
      setSelectedPunishmentFromGallery(null);
    } else {
      setSelectedPunishmentFromGallery(punishmentData);
      setSelectedCustomPunishment(null);
      setImagePreview(null);
    }
  }, [punishmentData]);

  useEffect(() => {
    if (isCustomPunishment) return;
    // Only setup observer if we have more items to load
    if (filteredPunishments.length <= displayCount) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Check if loader is visible and we're not already loading
        const target = entries[0];
        if (target.isIntersecting && !isLoadingMore) {
          setIsLoadingMore(true);
          // Small delay to prevent rapid multiple loads
          setTimeout(() => {
            setDisplayCount((prev) => prev + 12); // Load 12 more items
            setIsLoadingMore(false);
          }, 500);
        }
      },
      {
        root: null, // Use viewport as root
        rootMargin: "100px", // Start loading before reaching the end
        threshold: 0.1, // Trigger when even 10% of the loader is visible
      }
    );

    // Start observing the loader element
    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    // Cleanup observer on unmount
    return () => observer.disconnect();
  }, [
    filteredPunishments.length,
    displayCount,
    isLoadingMore,
    filteredCount,
    isFilterApplied,
    isCustomPunishment,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchPunishments();
    };
    const fetchDefaultCardImage = async () => {
      const data = await getDefaultPunishmentsImages();
      if (data.length > 0) {
        setDefaultCardImage(data[0].cardImageUrl || "");
      } else {
        console.warn("No default punishment images found.");
      }
    };
    fetchDefaultCardImage();
    fetchData();
  }, []);

  const fetchPunishments = async () => {
    try {
      const content = await getContentByType("punishment");
      setPunishments(content);
      setFilteredPunishments(content);
    } catch (error) {
      console.error("Error fetching punishments:", error);
    }
  };

  // Helper Functions
  const extractFLRatingLevel = (rating: string): number => {
    const match = rating.match(/Level\s*(\d+)/i);
    return match ? parseInt(match[1], 10) : 0;
  };

  const extractLevel = (flRating: string | undefined): number => {
    if (!flRating) return 1;

    // Check for format like "Cute & Fluffy (Level 1)"
    const levelMatch = flRating.match(/Level\s*(\d+)/i);
    if (levelMatch) {
      return parseInt(levelMatch[1]);
    }

    // Fallback check for format like "L1"
    const shortMatch = flRating.match(/L(\d+)/i);
    if (shortMatch) {
      return parseInt(shortMatch[1]);
    }

    return 1; // Default to level 1 if no match found
  };

  const extractPosition = (order: number) => {
    const itemOrder = order;
    if (itemOrder >= 0 && itemOrder < 25) {
      if (order < 10) {
        return "top_10";
      } else {
        return "top_25";
      }
    } else {
      return "";
    }
  };

  useEffect(() => {
    const filtered = punishments.filter((item) => {
      const selectedLevel =
        FL_RATING_OPTIONS.find((option) => option.value === selectedFLRating)
          ?.level || 0;

      const itemLevel = extractFLRatingLevel(item.flRating || "");

      const matchesSearch = item.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesEmbarrasment =
        embarrassmentLevel === 0 ||
        parseInt(item.embarrassmentLevel) === embarrassmentLevel;
      const matchesRisk =
        riskLevel === 0 || parseInt(item.riskLevel) === riskLevel;
      const matchesLongTerm =
        longTermEffects === 0 ||
        parseInt(item.longTermEffects) === longTermEffects;
      const matchesFLRating =
        selectedFLRating === "" || itemLevel === selectedLevel;

      const noFiltersActive =
        embarrassmentLevel === 0 &&
        riskLevel === 0 &&
        longTermEffects === 0 &&
        selectedFLRating === "" &&
        searchTerm.trim() === "";

      return (
        noFiltersActive ||
        (matchesSearch &&
          matchesEmbarrasment &&
          matchesRisk &&
          matchesLongTerm &&
          matchesFLRating)
      );
    });
    setFilteredPunishments(filtered);
    setFilteredCount(filtered.length);
  }, [
    searchTerm,
    embarrassmentLevel,
    riskLevel,
    longTermEffects,
    selectedFLRating,
    punishments,
    isFilterApplied,
  ]);

  const handlePunishmentSelection = (item: any) => {
    setSelectedPunishmentFromGallery(item);
  };

  // useEffect(() => {
  //   if (punishmentData?.isCustomPunishment !== undefined) {
  //     setIsCustomPunishment(leagueData.leaguePunishment.isCustomPunishment);
  //   }
  // }, [leagueData]);

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const validationError = validateImageFile(file);
      if (validationError) {
        alert(`${validationError}`);
        return;
      }

      try {
        const { resizedUrl } = await resizeImage(file);
        setTempImageUrl(resizedUrl);
        setShowCropper(true);
      } finally {
      }
    },
    []
  );

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea) return;
    const cropped = await cropImageFile(tempImageUrl, croppedArea);
    if (cropped) {
      setPunishmentImage(cropped);
      setImagePreview(URL.createObjectURL(cropped));
    }
    setShowCropper(false);
  }, [tempImageUrl, croppedArea]);

  // Form validity now determined in parent when deciding to persist

  // Build punishment object with validity flag to send to parent
  const buildPunishmentData = useCallback(() => {
    if (isCustomPunishment) {
      if (!selectedCustomPunishment) return null;
      const title = selectedCustomPunishment.title?.trim() || "";
      const description =
        selectedCustomPunishment.imageDescription?.trim() || "";
      const flRating = selectedCustomPunishment.flRating || "";
      const isValid = !!title && !!description && !!flRating; // require core fields
      return {
        ...selectedCustomPunishment,
        isCustomPunishment: true,
        cardImage: selectedCustomPunishment.cardImage || null,
        previewImage: imagePreview || null,
        localFile: punishmentImage || null,
        isValid,
      };
    }
    if (!selectedPunishmentFromGallery) return null;
    const isValid = !!selectedPunishmentFromGallery; // at least one selected
    return {
      ...selectedPunishmentFromGallery,
      isCustomPunishment: false,
      cardImage:
        selectedPunishmentFromGallery.cardImage ||
        selectedPunishmentFromGallery.image ||
        null,
      previewImage: null,
      localFile: null,
      isValid,
    };
  }, [
    isCustomPunishment,
    selectedCustomPunishment,
    selectedPunishmentFromGallery,
    imagePreview,
    punishmentImage,
  ]);

  // Guard against sending identical data objects upward repeatedly (prevents infinite update loop)
  const lastSentDataJsonRef = useRef<string | null>(null);
  useEffect(() => {
    if (typeof setPunishmentData !== "function") return;
    const data = buildPunishmentData();
    const json = JSON.stringify(data);
    if (json === lastSentDataJsonRef.current) return; // no meaningful change
    lastSentDataJsonRef.current = json;
    setPunishmentData(data);
  }, [buildPunishmentData, setPunishmentData]);

  return (
    <>
      <div className="pageHeading">
        <span className="headingItem">Select Punishment</span>
      </div>
      <div className="btnWrap">
        <button
          type="button"
          className={
            `btnStyle` + (!isCustomPunishment ? " btnHighlighted" : "")
          }
          onClick={() => {
            if (isCustomPunishment) {
              setIsCustomPunishment(false);
              setSelectedCustomPunishment(null);
              setImagePreview(null);
              setPunishmentImage(null);
              if (punishmentImageRef.current)
                punishmentImageRef.current.value = "";
            }
          }}
        >
          SELECT FROM GALLERY
        </button>
        <button
          type="button"
          className={`btnStyle` + (isCustomPunishment ? " btnHighlighted" : "")}
          onClick={() => {
            if (!isCustomPunishment) {
              setIsCustomPunishment(true);
              setSelectedPunishmentFromGallery(null);
              setImagePreview(null);
              setPunishmentImage(null);
              if (punishmentImageRef.current)
                punishmentImageRef.current.value = "";
            }
          }}
        >
          <IconEdit />
          CREATE YOUR OWN
        </button>
      </div>
      {/* Create Your Own Section */}
      {isCustomPunishment && (
        <div className="createYourOwnPunishmentInfo">
          <div className="formGroup">
            <label>
              Punishment Title <span className="reqTxt">Required</span>
            </label>
            <input
              value={selectedCustomPunishment?.title || ""}
              onChange={(e) =>
                setSelectedCustomPunishment({
                  ...selectedCustomPunishment,
                  title: e.target.value,
                })
              }
              maxLength={100}
              required
              className="fldStyle"
              type="text"
              placeholder="Enter punishment title"
            />
          </div>
          <div className="formGroup">
            {/* <textarea
              value={selectedCustomPunishment?.imageDescription || ""}
              maxLength={1000}
              required
              className="textareaStyle"
              placeholder="Enter punishment description"
              onChange={(e) => {
                setSelectedCustomPunishment({
                  ...selectedCustomPunishment,
                  imageDescription: e.target.value,
                });
              }}
            /> */}
            <MarkDownWithOption
              value={selectedCustomPunishment?.imageDescription}
              onChange={(value) =>
                setSelectedCustomPunishment({
                  ...selectedCustomPunishment,
                  imageDescription: value,
                })
              }
              required
              maxLength={1000}
              placeholder="Enter punishment description"
            />
          </div>
          <div className="formGroup">
            <div
              className="dropdownStyle"
              onClick={() => setShowDropdown(!showDropDown)}
            >
              <div className="selectedValue">
                {selectedCustomPunishment?.flRating ? (
                  <img
                    src={
                      selectedCustomPunishment?.flRating
                        ? badgeImages[
                            extractLevel(selectedCustomPunishment.flRating) - 1
                          ]
                        : ""
                    }
                    alt=""
                  />
                ) : (
                  ""
                )}

                <span>
                  {selectedCustomPunishment?.flRating ||
                    "Select Punishment Level"}
                </span>
              </div>
              {showDropDown && (
                <ul className="dropdownList">
                  {flRatingOptions.map((option, index) => (
                    <li
                      key={index}
                      onClick={() => {
                        setShowDropdown(false);
                        setSelectedCustomPunishment({
                          ...selectedCustomPunishment,
                          flRating: option.value,
                        });
                      }}
                    >
                      <img src={badgeImages[index]} alt="" />
                      <span>{option.value}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {showCropper && tempImageUrl && (
            <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
              <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
                <Cropper
                  image={tempImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={9 / 16}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
                <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                  <button
                    type="button"
                    onClick={() => setShowCropper(false)}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCropConfirm}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Crop & Save
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="uploadArea">
            {!imagePreview && (
              <>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  ref={punishmentImageRef}
                  onChange={handleImageChange}
                />
                <button
                  className="btnStyle btnHighlighted"
                  type="button"
                  onClick={() => punishmentImageRef.current?.click()}
                >
                  UPLOAD PUNISHMENT PHOTO
                </button>
              </>
            )}

            {imagePreview && (
              <div className="mt-2 relative inline-block">
                <img
                  src={imagePreview}
                  alt="Punishment Preview"
                  style={{ maxWidth: 100, borderRadius: 8 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setPunishmentImage(null);
                    setImagePreview(null);
                    if (punishmentImageRef.current) {
                      punishmentImageRef.current.value = "";
                    }
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                  style={{ lineHeight: 1, fontSize: 16 }}
                  aria-label="Remove image"
                >
                  ×
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gallery Section */}
      {!isCustomPunishment && (
        <div className="contentArea">
          <div className="searchBlk">
            <div className="wrapper">
              <div className="searchFldWrap">
                <input
                  type="text"
                  placeholder="Search Punishments"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <SearchPanel
                  embarrassmentLevel={embarrassmentLevel}
                  setEmbarrassmentLevel={setEmbarrassmentLevel}
                  riskLevel={riskLevel}
                  setRiskLevel={setRiskLevel}
                  longTermEffects={longTermEffects}
                  setLongTermEffects={setLongTermEffects}
                  setIsFilterApplied={setIsFilterApplied}
                  selectedFLRating={selectedFLRating}
                  setSelectedFLRating={setSelectedFLRating}
                />
              </div>
            </div>
          </div>
          <div className="innerWrapper">
            <div className="punishmentItems noGap">
              <ul className="productList">
                {filteredPunishments
                  .slice(0, displayCount)
                  .map((item, index) => {
                    const level = extractLevel(item.flRating);
                    const topPosition = extractPosition(item.order ?? -1);
                    const bgImage = `url(${
                      item.cardImage ?? item.image ?? defaultCardImage
                    })`;
                    return (
                      <li
                        key={item.id}
                        style={{
                          backgroundImage: bgImage || "",
                        }}
                        onClick={() => handlePunishmentSelection(item)}
                      >
                        <span
                          className={`itemCheck${
                            selectedPunishmentFromGallery &&
                            item.id === selectedPunishmentFromGallery.id
                              ? " active"
                              : ""
                          }`}
                          onClick={() => handlePunishmentSelection(item)}
                        >
                          <ItemCheck />
                        </span>
                        <div className="listLeftCont">
                          {topPosition && (
                            <div className={topPosition.replace(/_/g, "")}>
                              Top {topPosition.split("_")[1]}
                            </div>
                          )}
                          <div className="favItemHeading">{item.title}</div>
                          <div className={`levelBlk badge-${level}`}>
                            Level {level}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                {filteredPunishments.length > displayCount && (
                  <div
                    ref={loaderRef}
                    className="w-full flex justify-center items-center py-8"
                  >
                    {isLoadingMore ? (
                      <div className="w-8 h-8 border-4 border-[#18f7b1] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <div className="h-8" />
                    )}
                  </div>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* <button
        className="btnStyle bigBtn floatingBtn"
        disabled={!isFormValid() || isSaving}
        onClick={handleContinue}
      >
        CONTINUE
      </button> */}
    </>
  );
};

export default SelectPunishment;
